import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PulseHeader from '../components/PulseHeader';
import PulseLogo from '../components/PulseLogo';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';
import { signUp } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformDetection } from '@/utils/platform-utils.tsx';
import { Loader2 } from 'lucide-react';
import { Apple } from 'lucide-react';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle, signInWithApple } = useAuth();
  const { isAndroid, isIOS } = usePlatformDetection();
  
  // Get the returnTo parameter from the URL
  const queryParams = new URLSearchParams(location.search);
  const returnTo = queryParams.get('returnTo') || '/';
  
  // Helper function to safely decode returnTo
  const getDecodedReturnPath = (path: string) => {
    try {
      // Ensure we decode the path properly
      const decodedPath = decodeURIComponent(path);
      console.log('Signup component - Decoded path:', decodedPath);
      
      // Validate the path format
      if (!decodedPath.startsWith('/')) {
        // If somehow we got a malformed path, prepend slash
        console.log('Signup component - Adding missing slash to path');
        return '/' + decodedPath;
      }
      
      return decodedPath;
    } catch (e) {
      console.error('Signup component - Error decoding returnTo:', e);
      // Fallback to home page if decoding fails
      return '/';
    }
  };
  
  // Ensure returnTo is properly decoded for navigation
  const decodedReturnTo = getDecodedReturnPath(returnTo);
  
  // Debug logging
  useEffect(() => {
    console.log('Signup component - returnTo value:', returnTo);
    console.log('Signup component - decoded returnTo value:', decodedReturnTo);
    console.log('Signup component - search params:', location.search);
    
    // Check if returnTo starts with /chapter/ to ensure proper chapter redirection
    if (returnTo && returnTo.includes('/chapter/')) {
      console.log('Signup component - Chapter redirect detected. Chapter ID:', 
        returnTo.replace('/chapter/', ''));
    }
  }, [returnTo, decodedReturnTo, location.search]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log('Signup component - User already logged in, redirecting to:', decodedReturnTo);
      // Use a timeout to ensure auth context is fully updated
      setTimeout(() => {
        navigate(decodedReturnTo, { replace: true });
      }, 500); // Increased timeout to ensure auth is fully processed
    }
  }, [user, navigate, decodedReturnTo]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Log the returnTo parameter for debugging
    console.log('Signup form submission - returnTo parameter:', returnTo);
    console.log('Signup form submission - decoded returnTo:', decodedReturnTo);
    
    // Basic validation
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }
    
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const { user, error } = await signUp(email, password);
      
      if (error) {
        toast.error('Erro ao criar conta', {
          description: error
        });
      } else {
        // Toast messages are now shown in the auth service
        
        // Make sure to properly encode the returnTo parameter when passing to login
        console.log('Signup successful, redirecting to login with returnTo:', decodedReturnTo);
        
        // Clean validation - make sure we have a valid path
        const cleanReturnPath = decodedReturnTo.startsWith('/') 
          ? decodedReturnTo 
          : `/${decodedReturnTo}`;
          
        // Log the final path that will be encoded
        console.log('Signup - cleaned return path for login:', cleanReturnPath);
        
        // Encode the returnTo for login, which will be decoded there
        const encodedReturnForLogin = encodeURIComponent(cleanReturnPath);
        console.log('Signup - encoded returnTo for login page:', encodedReturnForLogin);
        
        // Navigate to login page with the preserved returnTo parameter
        // Add a small delay to ensure toast messages are seen
        setTimeout(() => {
          navigate(`/login?returnTo=${encodedReturnForLogin}`);
        }, 1000); // Longer delay to ensure user sees the toast messages
      }
    } catch (error) {
      const e = error as Error;
      toast.error('Ocorreu um erro ao criar a conta', {
        description: e.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-full-page-container bg-gradient-to-br from-rose-700 via-pink-700 to-purple-700 text-white">
      
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center pb-16">
        <PulseLogo size="lg" variant="white" />
        <p className="mt-2 mb-8">Seu app de romances</p>
        
        <form onSubmit={handleSignup} className="w-full space-y-6">
          <div className="space-y-2 text-left">
            <label htmlFor="email" className="block">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Insira o email"
              className="auth-input"
            />
          </div>
          
          <div className="space-y-2 text-left">
            <label htmlFor="password" className="block">Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          
          <button 
            type="submit" 
            className="pulse-btn-primary mt-8"
            disabled={loading}
          >
            {loading ? 'Cadastrando...' : 'Cadastre-se com E-Mail e Senha'}
          </button>
        </form>
        
        <div className="w-full max-w-sm">
          {isIOS ? null : (
            <>
              <button
                type="button"
                onClick={async () => {
                  setAppleLoading(true);
                  try {
                    await signInWithApple();
                  } catch (error) {
                    toast.error('Falha ao tentar cadastro com Apple.');
                    console.error('Apple sign-up error on Signup page:', error);
                  } finally {
                    setAppleLoading(false);
                  }
                }}
                className="pulse-btn-social w-full hidden"
                disabled={loading || googleLoading || appleLoading}
              >
                {appleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Apple className="mr-2 h-5 w-5" />
                )}
                Cadastrar com Apple
              </button>

              <div className="my-4 flex items-center">
                <div className="flex-grow border-t border-white/30"></div>
                <span className="px-4 text-white/80">OU</span>
                <div className="flex-grow border-t border-white/30"></div>
              </div>
            </>
          )}
        </div>

        {!isIOS && (
          <button
            type="button"
            onClick={async () => {
              setGoogleLoading(true);
              try {
                await signInWithGoogle();
              } catch (error) {
                toast.error('Falha ao tentar cadastro com Google.');
                console.error('Google sign-up error on Signup page:', error);
              } finally {
                setGoogleLoading(false);
              }
            }}
            className="pulse-btn-primary w-full max-w-sm mt-4"
            disabled={loading || googleLoading || appleLoading}
          >
            {googleLoading ? (
              'Redirecionando...'
            ) : (
              <span className="flex items-center justify-center">
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5 mr-2">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                Cadastre-se com o Google
              </span>
            )}
          </button>
        )}
        
        <p className="mt-8">
          Já tem uma conta?{' '}
          <Link 
            to={`/login${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`} 
            className="pulse-link"
          >
            Entre Aqui.
          </Link>
        </p>
        <p className="mt-4 text-xs text-white/80">
          Ao se cadastrar, você concorda com nossos <Link to="/terms-of-service" className="pulse-link underline">Termos de Serviço</Link> e <Link to="/privacy-policy" className="pulse-link underline">Política de Privacidade</Link>.
        </p>
      </div>
    </div>
  );
};

export default Signup;
